import React from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "component/ui/Dialog";
import { Button } from "component/ui/Button";
import CopyButton from "component/ui/CopyButton";
import { Loader2 } from "lucide-react";

/**
 * CompleteAccountOpeningModal
 * Modal 1: "Complete your account opening" payment modal.
 */
const CompleteAccountOpeningModal = ({
  isOpen = false,
  onClose,
  amount = "₦0",
  bankName = "",
  accountNumber = "",
  accountName = "",
  narration = "",
  isCrypto = false,
  network = "",
  address = "",
  onMoneySent,
}) => {
  if (!isOpen) return null;

  const displayNetwork = network || (isCrypto ? bankName : "");
  const displayAddress = address || (isCrypto ? accountNumber : "");
  const displayBankName = !isCrypto ? bankName : "";
  const displayAccountNumber = !isCrypto ? accountNumber : "";

  const allAccountDetailsText = isCrypto
    ? [
        displayNetwork ? `Network: ${displayNetwork}` : null,
        displayAddress ? `Address: ${displayAddress}` : null,
        amount ? `Amount to send: ${amount}` : null,
      ]
        .filter(Boolean)
        .join("\n")
    : [
        displayBankName ? `Bank name: ${displayBankName}` : null,
        displayAccountNumber ? `Account number: ${displayAccountNumber}` : null,
        accountName ? `Account name: ${accountName}` : null,
        amount ? `Amount to send: ${amount}` : null,
      ]
        .filter(Boolean)
        .join("\n");

  const cleanAmount = amount.replace(/[^0-9.]/g, "") || amount;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose && onClose()}>
      <DialogContent
        showCloseButton={true}
        className="max-w-[560px] w-full rounded-[16px] bg-white shadow-2xl overflow-hidden border-0"
      >
        <div className="p-6 sm:p-8">
          {/* Header */}
          <div className="flex flex-col items-center text-center">
            <DialogTitle className="text-xl sm:text-2xl font-semibold text-title-dark tracking-tight">
              Complete your account activation
            </DialogTitle>
            <DialogDescription className="text-sm text-slate-text font-[350] leading-relaxed px-1 mx-auto">
              Fund your wallet to cover the account opening fee
            </DialogDescription>
          </div>

          {/* Amount Card */}
          <div className="w-full bg-grey-bg rounded-lg py-4 px-6 flex flex-col items-center justify-center gap-1.5 mt-4">
            <span className="text-xs sm:text-sm font-normal text-label-text">
              Amount to send
            </span>

            <div className="flex items-center justify-center gap-2">
              <span className="text-3xl sm:text-[32px] font-medium text-main-dark tracking-tight font-sans">
                {amount}
              </span>
              <CopyButton
                text={cleanAmount}
                toastMessage="Amount copied"
                size={15}
                title="Copy amount"
              />
            </div>

            <div className="flex items-center gap-2 text-xs sm:text-sm text-label-text font-[400] mt-0.5">
              <Loader2 className="size-3.5 animate-spin text-label-text" />
              <span>Awaiting transfer</span>
            </div>
          </div>

          <div>
            <p className="text-sm font-medium text-gray-70 mt-4 mb-2 text-left">
              {isCrypto ? "Transfer to your wallet below" : "Transfer to your account below"}
            </p>

            {/* Account Details Card */}
            <div className="w-full bg-grey-bg rounded-[8px] p-4 sm:p-5 space-y-3.5">
              {isCrypto ? (
                <>
                  {/* Network */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-label-text font-normal">Network</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-90">{displayNetwork || "—"}</span>
                      {displayNetwork && (
                        <CopyButton
                          text={displayNetwork}
                          toastMessage="Network copied"
                          size={15}
                          title="Copy network"
                        />
                      )}
                    </div>
                  </div>

                  {/* Address */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-label-text font-normal">Address</span>
                    <div className="flex items-center gap-2 max-w-[70%]">
                      <span className="font-medium text-gray-90 truncate font-mono text-xs sm:text-sm">
                        {displayAddress || "—"}
                      </span>
                      {displayAddress && (
                        <CopyButton
                          text={displayAddress}
                          toastMessage="Address copied"
                          size={15}
                          title="Copy address"
                        />
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Bank name */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-label-text font-normal">Bank name</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-90">{displayBankName || "—"}</span>
                      {displayBankName && (
                        <CopyButton
                          text={displayBankName}
                          toastMessage="Bank name copied"
                          size={15}
                          title="Copy bank name"
                        />
                      )}
                    </div>
                  </div>

                  {/* Account number */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-label-text font-normal">Account number</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-90">{displayAccountNumber || "—"}</span>
                      {displayAccountNumber && (
                        <CopyButton
                          text={displayAccountNumber}
                          toastMessage="Account number copied"
                          size={15}
                          title="Copy account number"
                        />
                      )}
                    </div>
                  </div>

                  {/* Account name */}
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-label-text font-normal">Account name</span>
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-gray-90 text-right">{accountName || "—"}</span>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-solid border-card-bg flex p-6 sm:py-6 sm:px-8 items-center justify-between w-full">
          {/* Copy details link */}
          <CopyButton
            text={allAccountDetailsText}
            toastMessage={isCrypto ? "Wallet details copied to clipboard" : "Account details copied to clipboard"}
            size={18}
            className="text-main-dark hover:text-black"
          >
            {isCrypto ? "Copy wallet details" : "Copy account details"}
          </CopyButton>

          {/* Money has been sent button */}
          <Button
            type="button"
            variant="outline"
            onClick={onMoneySent}
            className="border border-solid border-secondary-90 hover:bg-card-bg text-main-dark font-medium text-sm rounded-[8px] px-5 h-11 transition-all cursor-pointer shadow-xs"
          >
            Money has been sent
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CompleteAccountOpeningModal;
